import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { AppConfigService } from 'src/app/app-config/app-config-service';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { ConfirmationDialogType } from 'src/app/common-components/confirmation-dialog/models/confirmation-dialog.config';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { DateTimeFilterFormatPipe } from 'src/app/pipes/datetime-filter-format-pipe/datetime-filter-format-pipe';
import { getFileUrlFromToken } from 'src/app/pipes/file-url-pipe/file-url';
import { FaxFilterOptions } from '../models/fax-filter-option';
import { FaxOrderingOptions, FaxRegisterOptions } from '../models/fax-register-options';
import { getFaxesQuery } from '../queries/fax.graphql';
import { GetFaxes, GetFaxesVariables } from '../queries/generated/GetFaxes';

@Injectable({ providedIn: 'root' })
export class FaxRegisterApiService {
   private dateTimePipe: DateTimeFilterFormatPipe;

   constructor(
      private httpClient: HttpClient,
      private apiService: LcsApiService,
      private confirmationService: ConfirmationDialogService,
      private appConfig: AppConfigService
   ) {
      this.dateTimePipe = new DateTimeFilterFormatPipe();
   }

   getFaxes(
      endCursor: string | null,
      filters: FaxFilterOptions[] | null,
      quickFilters: FaxRegisterOptions | null,
      orderingOptions: FaxOrderingOptions[] | null
   ): Observable<ExecutionResult<GetFaxes>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertFaxFilterOption.bind(this)),
              }
            : { filterOptions: [] };
      if (quickFilters) {
         if (quickFilters.startDate) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'CreateDate',
               operation: FilterOperations.GreaterThanOrEqualTo,
               value: this.dateTimePipe.transform(new Date(quickFilters.startDate)),
            });
         }
         if (quickFilters.endDate) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'CreateDate',
               operation: FilterOperations.LessThanOrEqualTo,
               value: this.dateTimePipe.transform(new Date(quickFilters.endDate)),
            });
         }
         if (quickFilters.typeName) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'FaxTypeId',
               operation: FilterOperations.EqualTo,
               value: quickFilters.typeName.typeName,
            });
         }
         if (quickFilters.statusName) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'FaxStatusId',
               operation: FilterOperations.EqualTo,
               value: quickFilters.statusName.typeName,
            });
         }
         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim().replace(/[-()\s]/g, '');
            const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };

            if (!isNaN(+quickFilters.searchText)) {
               subExpression.filterOptions.push({
                  field: 'From',
                  operation: FilterOperations.Contains,
                  value: quickFilters.searchText.trim(),
               });
               subExpression.filterOptions.push({
                  field: 'To',
                  operation: FilterOperations.Contains,
                  value: quickFilters.searchText.trim(),
               });
            } else {
               subExpression.filterOptions.push({
                  field: 'From',
                  operation: FilterOperations.Contains,
                  value: searchText,
               });
               subExpression.filterOptions.push({
                  field: 'To',
                  operation: FilterOperations.Contains,
                  value: searchText,
               });
            }
            filterBuilderFilterExpression.subExpressions = [subExpression];
         }
      }

      const variables = this.getFaxVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetFaxes, GetFaxesVariables>({
         query: getFaxesQuery,
         variables,
      });
   }

   private getFaxVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetFaxesVariables {
      const variables: GetFaxesVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }

   private convertFaxFilterOption(option: FaxFilterOptions): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: FaxFilterOptions): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'fax':
            return Array.isArray(option.value);
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }
   downloadFile(fileToken: string, fileName: string): void {
      const fileUrl = this.buildUrl(fileToken as string);
      this.httpClient.get(fileUrl, { responseType: 'blob' }).subscribe(
         (response: any) => {
            const url = window.URL.createObjectURL(response);
            const a = document.createElement('a');
            a.download = fileName.endsWith('.tiff') ? fileName : fileName + '.tiff';
            a.href = url;
            a.click();
            window.URL.revokeObjectURL(url);
         },
         (error) => {
            console.error(error);
            const message = 'Error downloading file.';
            this.openDialog(message);
         }
      );
   }

   openDialog(message: string): void {
      let dialogIsOpen = true;
      const dialogRef = this.confirmationService.confirm({
         data: {
            dialogType: ConfirmationDialogType.Ok,
            header: 'Could Not Download File',
            message: message,
         },
         size: DialogSize.Medium,
      });

      dialogRef.afterClosed().subscribe(() => {
         dialogIsOpen = false;
      });
   }
   private buildUrl(fileToken: string): string {
      return `${this.appConfig.getConfig().filereaderUrl}${getFileUrlFromToken(fileToken)}`;
   }
}
